<template>
  <Dialog
    v-model:visible="visibleModel"
    :header="dialogHeader"
    :style="{ width: '700px' }"
    :modal="true"
    @hide="onHide"
  >
    <!-- Step 1: Form - Input parameters -->
    <div v-if="autoSchedule.step.value === 'form'" class="form-content">
      <div class="form-group">
        <label>Generate schedule for</label>
        <Dropdown
          v-model="autoSchedule.targetWeek.value"
          :options="weekOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select a week..."
          class="w-full"
        />
      </div>

      <div class="form-group">
        <label>Notes for AI (optional)</label>
        <Textarea
          v-model="autoSchedule.guidance.value"
          rows="4"
          placeholder="e.g., 'Thursday closed for Thanksgiving', 'Max wants extra shifts'"
          class="w-full"
          :maxlength="1000"
        />
        <small class="help-text">
          Examples: "ensure 2 closers daily" · "busy Saturday morning" · "Alice on vacation"
        </small>
      </div>
    </div>

    <!-- Step 2: Loading - Generation in progress -->
    <div v-else-if="autoSchedule.step.value === 'loading'" class="loading-content">
      <div class="loading-spinner">
        <ProgressSpinner />
      </div>
      <div class="loading-text">
        <div class="loading-title">Generating schedule...</div>
        <div class="loading-subtitle">This may take 10-15 seconds</div>
      </div>
    </div>

    <!-- Step 3: Preview - Show generated schedule -->
    <div v-else-if="autoSchedule.step.value === 'preview'" class="preview-content">
      <!-- Day cards grid -->
      <div class="schedule-grid">
        <div
          v-for="day in sortedSchedule"
          :key="day.date"
          class="day-card"
          :class="{ 'day-closed': day.closed }"
        >
          <div class="day-header">
            <div class="day-name">{{ formatDayName(day.day) }}</div>
            <div class="day-date">{{ formatDateShort(day.date) }}</div>
          </div>
          <div class="day-content">
            <template v-if="day.closed">
              <div class="closed-badge">CLOSED</div>
              <div v-if="day.closed_reason" class="closed-reason">
                ({{ day.closed_reason }})
              </div>
            </template>
            <template v-else>
              <div
                v-for="shift in day.shifts"
                :key="`${shift.user_id}-${shift.start}`"
                class="shift-item"
              >
                <span class="shift-name">{{ shift.name }}</span>
                <span class="shift-time">
                  {{ formatTimeShort(shift.start) }}-{{ formatTimeShort(shift.end) }}
                </span>
                <i
                  v-if="shift.note"
                  class="pi pi-sparkles shift-note-icon"
                  v-tooltip.top="shift.note"
                ></i>
              </div>
              <div v-if="day.shifts.length === 0" class="no-shifts">
                No shifts
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div v-if="hasNotedShifts" class="legend">
        <i class="pi pi-sparkles"></i>
        <span>= adjusted from typical pattern</span>
      </div>

      <!-- AI Notes section -->
      <div v-if="autoSchedule.aiNotes.value" class="ai-notes-section">
        <div class="section-header">
          <i class="pi pi-comment"></i>
          <span>AI Notes</span>
        </div>
        <div class="ai-notes-content">
          {{ autoSchedule.aiNotes.value }}
        </div>
      </div>

      <!-- Unscheduled staff section -->
      <div
        v-if="autoSchedule.unscheduledStaff.value.length > 0"
        class="unscheduled-section"
      >
        <div class="section-header">
          <span>Not Scheduled This Week</span>
        </div>
        <div class="unscheduled-list">
          <div
            v-for="staff in autoSchedule.unscheduledStaff.value"
            :key="staff.user_id"
            class="unscheduled-item"
          >
            <span class="unscheduled-name">{{ staff.name }}</span>
            <span class="unscheduled-reason">
              <template v-if="staff.reason === 'no_history'">
                No prior shifts
              </template>
              <template v-else-if="staff.reason === 'unavailable_all_week'">
                Unavailable all week
              </template>
              <template v-else>
                Not included in schedule
              </template>
            </span>
          </div>
        </div>
        <small class="help-text">
          To include someone, add guidance like "Schedule Bob for Monday 9-5"
        </small>
      </div>

      <!-- Validation warnings section -->
      <div
        v-if="autoSchedule.validationWarnings.value.length > 0"
        class="warnings-section"
      >
        <div class="section-header">
          <i class="pi pi-exclamation-triangle"></i>
          <span>Validation Warnings</span>
        </div>
        <div class="warnings-list">
          <div
            v-for="(warning, index) in autoSchedule.validationWarnings.value"
            :key="index"
            class="warning-item"
          >
            {{ warning }}
          </div>
        </div>
      </div>

      <!-- Adjustment textarea -->
      <div class="form-group">
        <label>What should be different?</label>
        <Textarea
          v-model="autoSchedule.adjustment.value"
          rows="2"
          placeholder="e.g., 'don't schedule Max and Ava together'"
          class="w-full"
          :maxlength="500"
        />
      </div>

      <!-- Cache info (subtle) -->
      <div v-if="autoSchedule.cached.value" class="cache-info">
        <small>
          <i class="pi pi-clock"></i>
          Served from cache ({{ autoSchedule.generationTimeMs.value }}ms)
        </small>
      </div>
    </div>

    <!-- Footer actions -->
    <template #footer>
      <div class="dialog-footer">
        <!-- Form step -->
        <template v-if="autoSchedule.step.value === 'form'">
          <Button
            label="Cancel"
            icon="pi pi-times"
            @click="onCancel"
            class="p-button-text"
          />
          <Button
            label="Generate"
            icon="pi pi-sparkles"
            @click="onGenerate"
            :loading="autoSchedule.isGenerating.value"
            :disabled="!canGenerate"
          />
        </template>

        <!-- Loading step (no actions) -->
        <template v-else-if="autoSchedule.step.value === 'loading'">
          <!-- Empty footer during loading -->
        </template>

        <!-- Preview step -->
        <template v-else-if="autoSchedule.step.value === 'preview'">
          <Button
            label="Back"
            icon="pi pi-arrow-left"
            @click="autoSchedule.backToForm()"
            class="p-button-text"
          />
          <Button
            label="Regenerate"
            icon="pi pi-refresh"
            @click="onRegenerate"
            :loading="autoSchedule.isGenerating.value"
          />
          <Button
            label="Apply"
            icon="pi pi-check"
            @click="onApply"
            :loading="autoSchedule.isApplying.value"
            severity="success"
          />
        </template>
      </div>
    </template>
  </Dialog>

  <!-- Confirmation dialog for overwriting existing shifts -->
  <ConfirmDialog />
</template>

<script setup>
import { computed, watch, onMounted } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useAutoSchedule } from '@/composables/useAutoSchedule'
import { format, parseISO, addDays, startOfWeek } from 'date-fns'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  storeId: {
    type: Number,
    required: true
  },
  storeName: {
    type: String,
    default: ''
  },
  weekStartDay: {
    type: [Number, String],
    default: 1 // Monday = 1 (date-fns convention)
  }
})

// Convert weekStartDay string to number for date-fns
const weekStartDayNumber = computed(() => {
  if (typeof props.weekStartDay === 'number') {
    return props.weekStartDay
  }
  const dayMap = {
    'Sunday': 0,
    'Monday': 1,
    'Tuesday': 2,
    'Wednesday': 3,
    'Thursday': 4,
    'Friday': 5,
    'Saturday': 6
  }
  return dayMap[props.weekStartDay] ?? 1
})

const emit = defineEmits(['update:visible', 'scheduleApplied'])

const confirm = useConfirm()
const autoSchedule = useAutoSchedule()

// Two-way binding for visible
const visibleModel = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

// Dialog header changes based on step
const dialogHeader = computed(() => {
  if (autoSchedule.step.value === 'preview') {
    return 'Preview Schedule'
  }
  return 'Auto-Schedule'
})

// Generate week options (next 4-6 weeks from today)
const weekOptions = computed(() => {
  const options = []
  const today = new Date()
  const numWeeks = 6

  for (let i = 0; i < numWeeks; i++) {
    const weekStart = startOfWeek(addDays(today, i * 7), {
      weekStartsOn: weekStartDayNumber.value
    })
    const weekEnd = addDays(weekStart, 6)

    options.push({
      label: `Week of ${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`,
      value: format(weekStart, 'yyyy-MM-dd')
    })
  }

  return options
})

// Can generate if week is selected
const canGenerate = computed(() => {
  return !!autoSchedule.targetWeek.value
})

// Schedule with shifts sorted by start time
const sortedSchedule = computed(() => {
  if (!autoSchedule.schedule.value) return []
  return autoSchedule.schedule.value.map(day => ({
    ...day,
    shifts: [...(day.shifts || [])].sort((a, b) => {
      // Sort by start time (HH:MM string comparison works for 24h format)
      return a.start.localeCompare(b.start)
    })
  }))
})

// Check if any shifts have notes (for legend display)
const hasNotedShifts = computed(() => {
  if (!autoSchedule.schedule.value) return false
  return autoSchedule.schedule.value.some(day =>
    day.shifts?.some(shift => shift.note)
  )
})

// Format helpers
const formatDayName = (dayName) => {
  return dayName ? dayName.toUpperCase().slice(0, 3) : ''
}

const formatDateShort = (dateStr) => {
  try {
    const date = parseISO(dateStr)
    return format(date, 'M/d')
  } catch {
    return dateStr
  }
}

const formatTimeShort = (timeStr) => {
  // Convert "09:00" to "9" or "14:00" to "2pm"
  if (!timeStr) return ''
  const [hours, minutes] = timeStr.split(':')
  const hour = parseInt(hours)
  const min = minutes !== '00' ? `:${minutes}` : ''

  if (hour === 0) return `12${min}am`
  if (hour < 12) return `${hour}${min}`
  if (hour === 12) return `12${min}pm`
  return `${hour - 12}${min}pm`
}

// Initialize when modal opens
watch(visibleModel, (isVisible) => {
  if (isVisible) {
    autoSchedule.openModal()

    // Set default week to first option
    if (weekOptions.value.length > 0 && !autoSchedule.targetWeek.value) {
      autoSchedule.targetWeek.value = weekOptions.value[0].value
    }
  }
})

// Handle generate
const onGenerate = async () => {
  await autoSchedule.generate(props.storeId)
}

// Handle regenerate with adjustments
const onRegenerate = async () => {
  await autoSchedule.regenerate(props.storeId)
}

// Handle apply with overwrite confirmation
const onApply = async () => {
  // First attempt (might return requiresConfirmation)
  const result = await autoSchedule.apply(props.storeId, false)

  if (result.requiresConfirmation) {
    // Show confirmation dialog
    confirm.require({
      message: result.message,
      header: 'Confirm Overwrite',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Overwrite',
      rejectLabel: 'Cancel',
      accept: async () => {
        // User confirmed - apply with overwrite
        const confirmResult = await autoSchedule.apply(props.storeId, true)
        if (confirmResult.success) {
          // Notify parent to refresh calendar
          emit('scheduleApplied')
        }
      }
    })
  } else if (result.success) {
    // Applied successfully without confirmation needed
    emit('scheduleApplied')
  }
}

// Handle cancel
const onCancel = () => {
  visibleModel.value = false
}

// Handle dialog hide
const onHide = () => {
  // Reset is handled by composable when opening modal
}
</script>

<style scoped>
/* Form content */
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #333;
}

.help-text {
  color: #6b7280;
  font-size: 0.875rem;
}

.w-full {
  width: 100%;
}

/* Loading content */
.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  gap: 1.5rem;
}

.loading-spinner {
  /* ProgressSpinner handles its own styling */
}

.loading-text {
  text-align: center;
}

.loading-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
}

.loading-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Preview content */
.preview-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0;
  max-height: 70vh;
  overflow-y: auto;
}

/* Schedule grid - 4 cards on top row, 3 on bottom */
.schedule-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}

.day-card {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  background-color: white;
  min-height: 100px;
}

.day-card.day-closed {
  background-color: #f9fafb;
}

.day-header {
  background-color: #f3f4f6;
  padding: 0.5rem;
  border-bottom: 1px solid #d1d5db;
  text-align: center;
}

.day-name {
  font-weight: 700;
  font-size: 0.75rem;
  color: #374151;
  letter-spacing: 0.05em;
}

.day-date {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.125rem;
}

.day-content {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.closed-badge {
  text-align: center;
  font-weight: 700;
  color: #ef4444;
  padding: 0.5rem 0;
}

.closed-reason {
  text-align: center;
  font-size: 0.75rem;
  color: #6b7280;
}

.shift-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  padding: 0.125rem 0;
}

.shift-name {
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.shift-time {
  color: #6b7280;
  font-size: 0.75rem;
}

.shift-note-icon {
  color: #f59e0b;
  font-size: 0.75rem;
  margin-left: auto;
}

.no-shifts {
  text-align: center;
  color: #9ca3af;
  font-size: 0.75rem;
  padding: 0.5rem 0;
  font-style: italic;
}

/* Legend */
.legend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  padding: 0.5rem 0;
}

.legend .pi-sparkles {
  color: #f59e0b;
}

/* Section headers */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
}

/* AI Notes section */
.ai-notes-section {
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 1rem;
}

.ai-notes-section .section-header {
  color: #1e40af;
  margin-bottom: 0.5rem;
}

.ai-notes-section .section-header .pi-comment {
  color: #3b82f6;
}

.ai-notes-content {
  color: #1e40af;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

/* Unscheduled staff section */
.unscheduled-section {
  background-color: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 1rem;
}

.unscheduled-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.unscheduled-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.unscheduled-name {
  font-weight: 600;
  color: #92400e;
}

.unscheduled-reason {
  color: #92400e;
  font-size: 0.875rem;
}

.unscheduled-item::before {
  content: '•';
  color: #92400e;
}

/* Validation warnings section */
.warnings-section {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 1rem;
}

.warnings-section .section-header {
  color: #991b1b;
}

.warnings-section .section-header .pi-exclamation-triangle {
  color: #ef4444;
}

.warnings-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.warning-item {
  color: #991b1b;
  font-size: 0.875rem;
}

.warning-item::before {
  content: '• ';
  color: #ef4444;
}

/* Cache info */
.cache-info {
  text-align: center;
  color: #9ca3af;
  font-size: 0.75rem;
}

.cache-info .pi-clock {
  font-size: 0.625rem;
  margin-right: 0.25rem;
}

/* Dialog footer */
.dialog-footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

/* Scrollbar styling */
.preview-content::-webkit-scrollbar {
  width: 8px;
}

.preview-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.preview-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.preview-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive grid for smaller screens */
@media (max-width: 768px) {
  .schedule-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
